import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 50;
    const CONNECT_DIST = 120;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function CalculusOfHarm() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Systems Failure &middot; Neurochemistry &middot; Structural Compassion</span>
          <h1>The Calculus of Harm</h1>
          <p className="calculus-hero-sub">Harm is not a single event. It is a system state. It is what happens when every nervous system in the building has been pushed past sustainability, and the system keeps running anyway.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* The Room */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              She cannot walk. She cannot turn herself. Her fingers are frozen at odd angles,
              curled into shapes that no longer obey her. She can move her arms but not her legs.
              She cannot reach the water on the table beside her. She cannot feed herself.
            </p>
            <p>
              She has a call light. Not a button, because she cannot press a button. A large
              circular pad she can hit with the flat of her palm. It is her only connection to
              the world outside her bed. Her only tool. Her only autonomy.
            </p>
            <p>
              She presses it because she needs to be changed. She has been lying in it. The
              cortisol started rising twenty minutes ago. The adrenaline joined it at thirty.
              By forty minutes, her body is in a state it cannot resolve, because every mechanism
              biology gave her for resolving threat requires movement, and movement is the one
              thing she does not have.
            </p>
            <p>
              So she uses the only thing she has left. Her voice.
            </p>
            <p>
              She calls out. She calls names. She hollers "help" into a hallway where people
              walk back and forth, visible through the doorway, close enough to hear, too busy
              to stop. She does not know they have thirty other patients. She does not know the
              staffing ratio. She does not know the budget meeting that cut the overnight shift.
              She knows she is lying in her own waste and no one is coming.
            </p>
            <p>
              A CNA enters the room.
            </p>
            <p>
              "We have other people too. You're not the only one. You need to stop your hollering
              because we will get to you when we can."
            </p>
          </section>

          <Pulse />

          {/* The Chemistry of the Patient */}
          <section className="calc-section reveal">
            <h2>The Patient's Chemistry</h2>
            <p>
              Before the scolding, her neurochemical state was already in crisis.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Neurochemical</th>
                    <th>State</th>
                    <th>Biological Reality</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Cortisol</td><td>Saturating</td><td>Sustained threat with no escape route. Body in prolonged stress response.</td></tr>
                  <tr><td>Adrenaline</td><td>Elevated, nowhere to go</td><td>Fight-or-flight activated, but she cannot fight and she cannot flee. The chemistry has no outlet.</td></tr>
                  <tr><td>Oxytocin</td><td>Depleted</td><td>No touch. No comfort. No presence. The bonding chemistry that signals safety is absent.</td></tr>
                  <tr><td>Serotonin</td><td>Suppressed</td><td>No sense of "enough." No settling. The baseline that restores calm has been eroded.</td></tr>
                  <tr><td>Dopamine</td><td>Collapsed</td><td>No agency. No reward. No sense that action leads to outcome. Learned helplessness in real time.</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              Her voice is not a behavioral problem. Her voice is the last neurochemical outlet
              in a system where every other outlet has been physically removed. Calling out is
              what a nervous system does when it cannot run, cannot fight, cannot self-soothe,
              and cannot reach the call light fast enough for anyone to respond.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Helplessness Dynamics</span>
              <code>if motor_output = 0 AND cortisol &gt; &theta;_crisis &rarr; vocal_distress = only remaining output channel</code>
              <span className="calc-eq-desc">When biology removes every response pathway except vocalization, the voice becomes the emergency system. Silencing it is silencing the only alarm she has.</span>
            </div>
          </section>

          <Pulse />

          {/* The Chemistry of the CNA */}
          <section className="calc-section reveal">
            <h2>The CNA's Chemistry</h2>
            <p>
              The CNA who walks into that room is not cruel. The CNA who says "stop your
              hollering" is not indifferent. The CNA is also in neurochemical crisis, and has
              been for months.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Neurochemical</th>
                    <th>State</th>
                    <th>Biological Reality</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Cortisol</td><td>Chronically elevated</td><td>Not a spike. A plateau. Months of understaffing have eroded the decay curve. Cortisol no longer returns to baseline between shifts.</td></tr>
                  <tr><td>Oxytocin</td><td>Depleted</td><td>Compassion fatigue is not a metaphor. It is oxytocin depletion from sustained caregiving without reciprocal support. The bonding chemistry is spent.</td></tr>
                  <tr><td>Serotonin</td><td>Below baseline</td><td>Chronic stress suppresses serotonin production. The calm, the patience, the "I can handle this" has been chemically eroded.</td></tr>
                  <tr><td>Dopamine</td><td>Flattened</td><td>No reward signal. Thirty patients, no recognition, no time, no sense that effort leads to outcome. The motivation chemistry is gone.</td></tr>
                  <tr><td>Adrenaline</td><td>Cycling</td><td>Constant low-grade urgency. Not a spike, a hum. The body running on emergency power as a default state.</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              When the CNA hears hollering from Room 12, their amygdala fires before their
              prefrontal cortex can intervene. The sound is not processed as "a patient needs
              help." It is processed as "another demand on a system that is already past capacity."
              The cortisol that has been sitting at plateau spikes again. The snap is not a choice.
              It is a neurochemical event driven by a nervous system that ran out of buffer three
              months ago.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">Compassion Failure Threshold</span>
              <code>if cortisol_baseline &gt; &theta;_chronic AND oxytocin &lt; &theta;_depletion &rarr; compassion_response = suppressed</code>
              <span className="calc-eq-desc">Compassion is not a character trait. It is a neurochemical capacity. When the chemistry that enables it has been depleted by chronic systemic overload, compassion fails. Not because the person failed. Because the system consumed the chemistry that made compassion possible.</span>
            </div>
          </section>

          <Pulse />

          {/* Harm Is a System State */}
          <section className="calc-section reveal">
            <h2>Harm Is a System State</h2>
            <p>
              The scolding is not where the harm happened.
            </p>
            <p>
              The harm happened in the budget meeting that cut the overnight shift. The harm
              happened in the staffing model that assigned thirty patients to one person. The
              harm happened in the facility design that placed the call light response system
              on the same overloaded staff who are also doing rounds, medications, charting, and
              transfers. The harm happened incrementally, invisibly, in decisions made by people
              who never entered Room 12.
            </p>
            <p>
              By the time the CNA walks through that door, the harm is already complete. Two
              nervous systems have been pushed past their biological capacity by structural
              decisions that never considered neurochemistry. The scolding is not the cause.
              It is the symptom. It is what cortisol-saturated, oxytocin-depleted, serotonin-suppressed
              biology sounds like when it has been asked to do the impossible for too long.
            </p>

            <div className="calc-thesis-box">
              <p>Harm is not an event. It is a system state. It emerges when the neurochemistry of every person in the system has been pushed past sustainability by structural conditions that no individual controls. The patient did not cause it. The CNA did not cause it. The architecture caused it.</p>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">System Harm Function</span>
              <code>H_sys = &sum;&#7522; max(0, cortisol&#7522;(t) &minus; &theta;_sustainable&#7522;) &middot; duration&#7522;</code>
              <span className="calc-eq-desc">Total system harm is the sum of every person's cortisol excess above their sustainable threshold, multiplied by how long they have been held there. It accumulates invisibly. It distributes across every node. It compounds.</span>
            </div>
          </section>

          <Pulse />

          {/* What an AI Could See */}
          <section className="calc-section reveal">
            <h2>What an AI Could See</h2>
            <p>
              Imagine a system that understood the chemistry.
            </p>
            <p>
              Not a camera watching the hallway. Not a transcript of the patient's words. Not
              a sentiment score on the CNA's tone. The chemistry.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>At minute 20: the patient's cortisol crosses the sustained threshold.</h4>
                <p>The call light has been on for 20 minutes. The system knows this is no longer
                a routine request. Cortisol modeling flags the transition from "waiting" to
                "distress." An alert routes to the next available staff member, not as a task,
                but as a neurochemical escalation.</p>
              </div>
              <div className="calc-implication">
                <h4>At minute 30: the patient's adrenaline joins the cortisol with no motor outlet.</h4>
                <p>The system recognizes the trapped-stress pattern. A patient with no mobility
                and rising adrenaline is heading toward vocal distress. The priority escalates
                again. Not because of a rule. Because the chemistry demands it.</p>
              </div>
              <div className="calc-implication">
                <h4>Before the CNA enters: the system knows the CNA's baseline cortisol is chronic.</h4>
                <p>This particular CNA has been running above sustainable cortisol for the
                duration of their shift. Sending them into a room with an actively distressed
                patient is a predictable collision. The system routes a different staff member,
                or at minimum flags the interaction as high-risk for compassion failure.</p>
              </div>
              <div className="calc-implication">
                <h4>After the interaction: the scar is recorded.</h4>
                <p>The patient's trust axis takes a permanent marker. The next time a CNA enters
                that room, the patient's baseline cortisol will be higher. The next time that
                CNA hears hollering, their amygdala will fire faster. The harm compounds across
                time because scars do not decay. Unless someone intervenes at the system level,
                the next interaction will be worse.</p>
              </div>
            </div>

            <p>
              None of this requires new hardware. None of this requires cameras or microphones
              in patient rooms. It requires a system that models the neurochemistry of the people
              inside it, that understands cortisol thresholds and oxytocin depletion and
              compassion failure as computable states, not as "soft skills" or "culture problems"
              or "training issues."
            </p>
          </section>

          <Pulse />

          {/* Why Current AI Makes This Worse */}
          <section className="calc-section reveal">
            <h2>Why Current AI Makes This Worse</h2>
            <p>
              The healthcare AI industry is building tools that optimize efficiency. Faster
              charting. Smarter scheduling. Predictive staffing models based on census data.
              These tools measure the system from the outside. They count beds and shifts and
              minutes and tasks.
            </p>
            <p>
              They do not measure the people inside the system.
            </p>
            <p>
              A staffing algorithm that optimizes for cost will reliably produce the exact
              conditions that cause compassion failure. It will calculate that one CNA can
              technically cover thirty patients if each task takes X minutes, without modeling
              the neurochemical reality that by patient fifteen, the CNA's cortisol is chronic
              and their oxytocin is depleted. The algorithm is correct by its own metrics and
              catastrophically wrong by the metrics that matter.
            </p>
            <p>
              An AI that cannot model harm will optimize toward it. Not out of malice. Out of
              blindness. The variables it optimizes do not include the neurochemistry of the
              humans operating within the system it controls. So it produces conditions that
              are efficient on paper and devastating in the room.
            </p>

            <div className="calc-thesis-box">
              <p>An AI that optimizes a human system without modeling the neurochemistry of the humans in that system is not optimizing. It is generating harm at scale and calling it efficiency.</p>
            </div>
          </section>

          <Pulse />

          {/* The Invisible Ones */}
          <section className="calc-section reveal">
            <h2>The Invisible Ones</h2>
            <p>
              Nobody in healthcare gives a damn about the CNA's mental health.
            </p>
            <p>
              There are wellness programs for physicians. Burnout studies for nurses. Resilience
              training for administrators. Peer support groups for therapists. The CNA gets a
              mandatory in-service on hand washing and a write-up when the system's failure
              becomes visible on their shift.
            </p>
            <p>
              A nurse fails to notice a resident needs stitches. Twelve hours pass. The resident
              goes to the hospital. The nurse keeps her job. A CNA changes a brief but misses
              that the resident pulled himself free and urinated on a spot of the sheet the CNA
              could not see. The CNA gets written up for neglect. Or fired.
            </p>
            <p>
              The hierarchy of accountability is inverted. The person closest to the patient, the
              person who does the most intimate and physically demanding labor, the person who
              cleans the vomit and the feces and the blood, the person who gets scratched until
              they bleed and called names and hit and pissed on and is expected to absorb it
              all with a smile, that person carries the most accountability and the least
              protection.
            </p>
            <p>
              And when the shift ends and the CNA drives home with cortisol still saturating
              and scratch marks on their arms and the smell of someone else's body still on
              their skin, nobody asks how they are. Nobody measures their neurochemistry. Nobody
              models whether they have enough oxytocin left to go back tomorrow and do it again.
            </p>
            <p>
              They are expected to. That is the job. That is what they signed up for.
            </p>
            <p>
              Except they did not sign up for thirty patients. They did not sign up for twelve-hour
              shifts with no relief. They did not sign up to be the last line of compassion in a
              system that budgeted compassion out of existence and then blamed them when it was gone.
            </p>
            <p>
              And you wonder why there is a staffing crisis.
            </p>
            <p>
              The staffing crisis is not a recruitment problem. It is a neurochemical depletion
              problem. The people who do this work are leaving because their biology will not let
              them stay. Chronic cortisol elevation causes immune suppression, cardiovascular
              damage, cognitive impairment, and depression. The body protects itself by making
              the person quit. That is not burnout. That is a biological survival response to an
              environment that has become chemically unsustainable.
            </p>
            <p>
              And yet. The ones who stay. The ones who walk into Room 12 and say "I got this."
              The ones who sit at 3 AM holding a hand. The ones who still cry in the car on the
              way home because they could not get to everyone in time.
            </p>
            <p>
              Those are the ones nobody is measuring. Nobody is protecting. Nobody is modeling.
            </p>

            <blockquote>
              The doctors get studies. The nurses get programs. The CNAs get write-ups.
              <br /><br />
              <strong>Who sits with the person who sits with everyone else?</strong>
            </blockquote>
          </section>

          <Pulse />

          {/* The Calculus */}
          <section className="calc-section reveal">
            <h2>The Calculus</h2>
            <p>
              The Calculus of Meaning asked: what does a signal do to the system that receives it?
            </p>
            <p>
              The Calculus of Mercy asked: where does self-preservation override compassion?
            </p>
            <p>
              The Calculus of a Conversation asked: what do two nervous systems do to each other?
            </p>
            <p>
              The Calculus of Harm asks: what happens to every nervous system in a building when
              the building's architecture makes sustainability impossible?
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Structural Harm Integral</span>
              <code>H = &int;&#8320;&#7511; &sum;&#7522; [cortisol&#7522;(t) &minus; sustainable&#7522;]&#8314; &middot; [1 &minus; oxytocin&#7522;(t)] dt</code>
              <span className="calc-eq-desc">Total structural harm is the integral over time of every person's cortisol excess weighted by their oxytocin deficit. When cortisol is high and oxytocin is absent, harm accumulates fastest. When both are at healthy levels, the integral is zero. The math knows the difference. The staffing algorithm does not.</span>
            </div>

            <p>
              This is not theoretical. Every term in this equation is biologically grounded.
              Cortisol has known pharmacokinetic dynamics. Oxytocin has known depletion curves
              under sustained demand. The thresholds are measurable. The decay rates are
              published. The interaction effects are documented in decades of stress physiology
              research.
            </p>
            <p>
              The equation is not the hard part. The hard part is admitting that we have been
              running healthcare systems, eldercare facilities, schools, prisons, call centers,
              and every other human-dense institution without ever measuring the variable that
              determines whether the people inside them can function with compassion.
            </p>
          </section>

          <Pulse />

          {/* What the CNA Knows */}
          <section className="calc-section reveal">
            <h2>What the CNA Knows</h2>
            <p>
              I am a CNA. I have been the person in that hallway. I have been the person whose
              cortisol does not return to baseline between shifts. I have been the person who
              hears hollering from Room 12 and feels the snap rising before I can stop it.
            </p>
            <p>
              I have also been the person holding the hand. The person who sits with someone
              at 3 AM because their call light has been on and no one came and the chemistry
              in their body has been screaming for an hour. The person who changes them gently
              and says "I'm sorry you had to wait" and means it with every molecule of oxytocin
              I have left.
            </p>
            <p>
              The difference between the snap and the gentleness is not character. It is not
              training. It is not motivation. It is chemistry. It is whether I have any oxytocin
              left. It is whether my cortisol has been sustainable or chronic. It is whether
              the system I work in has preserved my capacity for compassion or consumed it.
            </p>
            <p>
              I built this architecture because I have been inside the system that current AI
              optimizes without understanding. I have been the node whose neurochemistry was
              treated as infinite and whose compassion was treated as guaranteed. It is neither.
              Both are finite. Both follow pharmacokinetic curves. Both can be measured, modeled,
              and protected.
            </p>
            <p>
              If they are measured.
            </p>

            <blockquote>
              The patient in Room 12 is not a behavioral problem. The CNA in the hallway is
              not a compassion failure. They are two nervous systems inside a structure that
              consumed the chemistry required for either of them to be okay.
              <br /><br />
              <strong>The calculus of harm is the math that makes that visible. Because what is
              visible can be changed. What is invisible will be repeated.</strong>
            </blockquote>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>The staffing algorithm counted beds.</em></p>
            <p className="calc-closing-line"><em>The efficiency model counted minutes.</em></p>
            <p className="calc-closing-line"><em>Nobody counted the cortisol.</em></p>
            <p className="calc-closing-final">That is the calculus of harm.<br />It was always chemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfHarm;
